// SURICATA design tokens.
//
// design-source/suricata-design-language.html is the SOURCE OF TRUTH for every
// value. Those values are quoted verbatim into src/index.css; this file only
// names the roles, and every entry below is a var() reference into that block.
// Nothing here defines a colour. DESIGN.md explains the rules.
//
// Because every value is a var() string, a component that consumes UI.* is
// automatically theme-correct in both themes, including inline SVG attributes
// (stroke/fill accept var() — verified in headless Chrome) and recharts props.
// The one thing that is now impossible is alpha-suffix concatenation like
// `${UI.accent.base}66` — use the purpose-named token (accent.hairline,
// lossLine, …) instead.

export const T = {
  // Legacy aliases — older screens still read these; they resolve to the same
  // variables as UI so the two can never drift.
  bg:        "var(--si-surface-page)",
  bgAlt:     "var(--si-surface-sunken)",
  card:      "var(--si-surface-raised)",
  cardAlt:   "var(--si-surface-sunken)",
  border:    "var(--si-line-hairline)",
  borderStrong: "var(--si-line-strong)",

  text:      "var(--si-text-primary)",
  textSub:   "var(--si-text-secondary)",
  textMuted: "var(--si-text-muted)",

  accent:    "var(--si-accent-base)",
  accentDeep:"var(--si-accent-base)",
  accentBg:  "var(--si-accent-soft)",
  accentBgStrong: "var(--si-accent-hairline)",

  green:     "var(--si-gain)",
  greenBg:   "var(--si-gain-soft)",
  red:       "var(--si-loss)",
  redBg:     "var(--si-loss-soft)",

  shadow:     "var(--si-shadow-sm)",
  shadowMd:   "var(--si-shadow-md)",
  shadowCard: "var(--si-shadow-card)",

  // Radius — the spec's scale (--r-*), verbatim.
  //   sm 6 chips · md 9 buttons · lg 10 nav items · xl 14 cards
  //   2xl 18 anchor card · pill 999
  radius: { sm: 6, md: 9, lg: 10, xl: 14, xxl: 18, pill: 999 },

  // Spacing scale — one 4/8/16/24 rhythm for card padding and gaps. Generous
  // space is the "אור יום" way to separate content; boxes and shadows are not.
  // The spec's 15-step scale (--sp-1..15). The xs/sm/md/lg aliases below it
  // are the names the existing components already use.
  scale: [4, 6, 8, 10, 12, 14, 16, 18, 22, 24, 26, 30, 34, 40, 44],
  //   xs 4 · sm 8 · md 16 · lg 24
  space: { xs: 4, sm: 8, md: 16, lg: 24 },
};

// Convenience alias for the spacing scale (import { SPACE }).
export const SPACE = T.space;

// ─── UI — the semantic palette, by role ──────────────────────────────────────
// Every key is named for what it *is used for*, never for its hue. Swapping a
// value (in index.css) must never require renaming a key here.
export const UI = {
  // Surfaces — warm ivory-sand in day, warm charcoal in night. Each step is
  // deliberately small so cards separate by edge and space, not by glow.
  surface: {
    page:    "var(--si-surface-page)",
    raised:  "var(--si-surface-raised)",   // panels, rows, inner surfaces
    // The card ground. Borderless: day separates by elevation, night by one
    // step up the surface ramp (index.css, 2026-09-23). Pair it with
    // UI.shadow.cardEl and no border.
    card:    "var(--si-surface-card)",
    sunken:  "var(--si-surface-sunken)",   // wells/insets inside a card
    overlay: "var(--si-surface-overlay)",  // modals, popovers, sheets
    // THE anchor. A light theme needs one dark surface for the eye to rest
    // on, or every card floats and the screen reads as one flat sheet. One
    // per screen — the hero — and never two.
    anchor:  "var(--si-surface-anchor)",
  },

  // Selected / active. The neutral state colour the interface was missing:
  // enforcing "green and red are money only" left nothing to mark a chosen
  // filter with, so everything read grey. Deep sand — clearly felt against
  // both the page and a card, carrying no financial meaning at all, and
  // never the gold (gold is insight, Pro and the one primary action).
  state: {
    surface: "var(--si-state-surface)",   // --selected-bg
    line:    "var(--si-state-line)",
    fg:      "var(--selected-fg)",        // text ON the selected surface
  },
  // Alias kept so the call sites written against it stay valid; it resolves
  // to state.surface.
  fill: {
    subtle: "var(--si-fill-subtle)",
  },

  // Borders read as hairlines, not rules. `strong` is for a focused or
  // selected edge — not for emphasis by decoration.
  line: {
    hairline: "var(--si-line-hairline)",
    strong:   "var(--si-line-strong)",
  },

  // Text ramp — deep warm brown primary (day) / warm ivory (night).
  // Three levels that separate at a glance rather than under a ruler —
  // luminance roughly doubles at each step (day 1 → 7.5 → 13).
  text: {
    primary:   "var(--si-text-primary)",
    secondary: "var(--si-text-secondary)",
    muted:     "var(--si-text-muted)",
    onAccent:  "var(--si-text-on-accent)",     // on a filled-gold surface
    onAnchor:  "var(--on-anchor)",     // on surface.anchor
    onAnchor2: "var(--on-anchor-2)",   // secondary, on surface.anchor
    onAnchor3: "var(--on-anchor-3)",   // meta, on surface.anchor
  },

  // Gold, split by job (the spec keeps two values, not one):
  //   base — --brand-text, gold TEXT on sand. AA 4.7:1.
  //   live — --brand, the fill/mark gold. Chips, dots, marks, and any gold on
  //          the anchor, where .si-anchor swaps `base` to it automatically.
  // Permitted uses, and no others: AI insight surfaces · Pro · THE one primary
  // action · the horizon marker on a flat day. Never a link (that is
  // `interactive`), never a selected state (that is `state`), never decoration.
  // Split by ground. `base` is the deepened gold that clears AA on the light
  // surfaces; `live` is the brand gold #C9A227, which appears ONLY on
  // surface.anchor (and everywhere in night, where the two are the same
  // value). On ivory #C9A227 scores 4.33:1 and cannot carry text; on the
  // anchor it scores 6.96:1 and is finally the gold the brand means.
  accent: {
    base:     "var(--si-accent-base)",   // --brand-text
    live:     "var(--si-accent-live)",   // --brand
    soft:     "var(--si-accent-soft)",
    hairline: "var(--si-accent-hairline)",
  },

  // Links, controls and anything the user can act on — a token of its own, so
  // interaction never has to borrow the gold. The spec's --interactive.
  interactive: "var(--interactive)",
  interactiveSoft: "var(--si-interactive-soft)",

  // Profit and loss only. Not "good/bad", not status, not validation —
  // a green success toast must use text tokens, not this.
  gain:     "var(--si-gain)",
  gainSoft: "var(--si-gain-soft)",
  loss:     "var(--si-loss)",
  lossSoft: "var(--si-loss-soft)",
  lossLine: "var(--si-loss-line)",   // border-grade loss (was `${loss}55` concat)

  // Learning feedback — right / wrong / completed in the Academy. A family of
  // its own, about 22° away in hue from gain and loss (teal rather than the
  // money green, rose-crimson rather than the money red) so a quiz answer can
  // never be mistaken for a position moving. Decided by David, 2026-09-06.
  learn: {
    correct:     "var(--si-learn-correct)",
    correctSoft: "var(--si-learn-correct-soft)",
    wrong:       "var(--si-learn-wrong)",
    wrongSoft:   "var(--si-learn-wrong-soft)",
  },

  // Caution — "this one is different, read it before you act". Never money and
  // never insight. One control per screen, at most.
  caution:     "var(--si-caution)",
  cautionSoft: "var(--si-caution-soft)",
  cautionLine: "var(--si-caution-line)",

  // Ordered ramp — for scales, never for identities. Index 0 carries the most
  // contrast against the page in both themes.
  series: [
    "var(--si-series-0)", "var(--si-series-1)", "var(--si-series-2)",
    "var(--si-series-3)", "var(--si-series-4)",
  ],

  // Categorical hues — for identities, never for scales. Eleven named slots;
  // per-theme values keep every one AA-legible as pill text on its surfaces
  // and at least 36° away from the three reserved hues (gold, gain, loss).
  categorical: [
    "var(--si-cat-0)", "var(--si-cat-1)", "var(--si-cat-2)", "var(--si-cat-3)",
    "var(--si-cat-4)", "var(--si-cat-5)", "var(--si-cat-6)", "var(--si-cat-7)",
    "var(--si-cat-8)", "var(--si-cat-9)", "var(--si-cat-10)",
  ],

  // Rank badges (1st / 2nd / 3rd). Metallic metaphor with no yellow — the
  // brand gold stays unique to insight, Pro and the primary action.
  medal: {
    first:  "var(--si-medal-1)",
    second: "var(--si-medal-2)",
    third:  "var(--si-medal-3)",
  },

  // The dim behind a modal. Warm-brown in day, near-black in night — a cold
  // grey scrim over ivory reads as a rendering fault, not as depth.
  scrim: "var(--si-scrim)",

  // Object-owned colours: these belong to a *thing*, not to the page, so they
  // do not flip with the theme the way a surface does.
  //   logoPlate — the cream square the Suricata mark is drawn on.
  logoPlate: "var(--si-logo-plate)",

  // Chart furniture — the grid is furniture, never data. One hairline value
  // per theme so a chart never carries a heavier grid than the app's own rules.
  chartGrid: "var(--si-chart-grid)",

  // The equity curve on the anchor: neutral cream, never gold — it is data,
  // not brand (the spec is explicit about this).
  chartLine: "var(--chart-line)",
  chartFill: "var(--chart-fill-top)",

  // Elevation, by role rather than by pixel values.
  shadow: {
    card:   "var(--si-shadow-card)",
    cardEl: "var(--si-shadow-card-el)",  // the borderless card's elevation
    pop:    "var(--si-shadow-pop)",     // popovers, autocompletes, tooltips
    modal:  "var(--si-shadow-md)",
    anchor: "var(--si-shadow-anchor)",  // the one hero card
    logo:   "var(--si-shadow-logo)",    // the Suricata mark on its plate
  },
};

// ─── Holdings palette ────────────────────────────────────────────────────────
// One fixed list for the holdings ring (AllocationRing). Assigned by position
// weight, descending · never by hash and never at random. Since sprint 16:
// seven hues that are really different from each other (cyan to magenta),
// with alternating lightness so they stay apart for colour-blind readers, and
// none of them green, red, orange or gold. The measurements are in index.css.
// Slot identity is stable: holding #1 is the same slot, the same hue, in both
// themes. Everything past the seventh is one grey slice, "אחרות".
export const HOLDING_COLORS = [
  "var(--si-hold-0)", "var(--si-hold-1)", "var(--si-hold-2)", "var(--si-hold-3)",
  "var(--si-hold-4)", "var(--si-hold-5)", "var(--si-hold-6)",
];

// The slice for everything past the top seven.
export const HOLDING_REST = "var(--si-hold-rest)";

// ─── Buttons ─────────────────────────────────────────────────────────────────
// Two canonical frames only, so actions read consistently across the app:
//   BTN.primary   — THE one gold action per screen: 1px accent outline, accent
//                   text; accent fill + on-accent text on hover (add
//                   className="si-btn-gold" for the hover behavior — both
//                   states read the same variable, so the gold can never
//                   shift shade under the cursor).
//   BTN.secondary — everything else: a solid hairline, never dashed.
// A dashed border is reserved for the single semantic case of a file drop zone.
export const BTN = {
  primary: {
    background: "transparent", color: UI.accent.base, border: `1px solid ${UI.accent.base}`,
    // --r-md, the spec's BUTTON step. This was T.radius.sm, the chip step, so
    // the one radius the scale defines for buttons had no consumers at all.
    borderRadius: T.radius.md, padding: "10px 18px",
    fontWeight: 700, fontSize: 15, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
  secondary: {
    background: UI.surface.page, color: UI.text.secondary, border: `1px solid ${UI.line.hairline}`,
    borderRadius: T.radius.md, padding: "10px 18px",
    fontWeight: 700, fontSize: 15, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
};

// Font stacks — all three families ship as local woff2 subsets from
// /public/fonts (hebrew+latin only); no network font requests. See DESIGN.md.
//   display — Heebo 700/800: headings.
//   body    — Assistant: UI text, controls, running copy.
//   num     — Archivo with tabular-nums: every price / percent / timestamp.
export const FONT = {
  display: "var(--font-display)",   // Heebo 700/800 — headings
  heading: "var(--font-display)",
  body:    "var(--font-ui)",        // Assistant — UI text
  latin:   "var(--font-num)",       // Archivo — tickers, timestamps
  num:     "var(--font-num)",       // Archivo — every figure, tabular-nums
};

// The spec's type scale, verbatim. Sizes are strings so a component can drop
// one straight into a style object and stay in step with the source.
export const TYPE_SCALE = {
  hero:     { fontSize: "var(--fs-hero)",    lineHeight: "var(--lh-hero)",   letterSpacing: "var(--tracking-hero)" },
  heroM:    { fontSize: "var(--fs-hero-m)",  lineHeight: "var(--lh-hero-m)", letterSpacing: "var(--tracking-hero)" },
  metric:   { fontSize: "var(--fs-metric)",  lineHeight: "var(--lh-metric)" },
  h1:       { fontSize: "var(--fs-h1)",      lineHeight: "var(--lh-h1)",     letterSpacing: "var(--tracking-h1)" },
  h2:       { fontSize: "var(--fs-h2)",      lineHeight: "var(--lh-h2)" },
  h3:       { fontSize: "var(--fs-h3)",      lineHeight: "var(--lh-h3)" },
  h3sm:     { fontSize: "var(--fs-h3-sm)",   lineHeight: "var(--lh-h3)" },
  body:     { fontSize: "var(--fs-body)",    lineHeight: "var(--lh-body)" },
  bodySm:   { fontSize: "var(--fs-body-sm)", lineHeight: "var(--lh-body-sm)" },
  ui:       { fontSize: "var(--fs-ui)",      lineHeight: "var(--lh-ui)" },
  uiSm:     { fontSize: "var(--fs-ui-sm)" },
  meta:     { fontSize: "var(--fs-meta)",    lineHeight: "var(--lh-meta)" },
  numRow:   { fontSize: "var(--fs-num-row)" },
  numPill:  { fontSize: "var(--fs-num-pill)" },
  numMicro: { fontSize: "var(--fs-num-micro)" },
};

// Pill styles for context tags on news / events / portfolio overlap.
// Three semantic tones — keep meaning fixed: hot = direct portfolio hit,
// geo  = sector or theme relevance, ok = no overlap (neutral, dim).
export const PILL = {
  base: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12.5,          // --fs-ui-sm, from the source scale
    fontWeight: 700,
    
    padding: "3px 10px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  // `hot` means "this event touches something you hold" — a status, not a
  // number that moved, so it is neutral-strong: primary text on the strongest
  // neutral fill, louder than `geo` without borrowing any reserved hue.
  hot: {
    color: UI.text.primary,
    background: "var(--si-pill-hot-bg)",
    border: `1px solid ${UI.line.strong}`,
  },
  // `geo` is relevance, not profit, loss or emphasis — a categorical hue,
  // never the reserved gold.
  geo: {
    color: "var(--si-pill-geo-fg)",
    background: "var(--si-pill-geo-bg)",
    border: "1px solid var(--si-pill-geo-line)",
  },
  // `ok` means "no overlap" — a stated finding, one step brighter than
  // `neutral`, which is quiet furniture (a ticker chip, an empty-state line).
  ok: {
    color: UI.text.secondary,
    background: "var(--si-pill-ok-bg)",
    border: `1px solid ${UI.line.strong}`,
  },
  neutral: {
    color: UI.text.muted,
    background: UI.line.hairline,
    border: `1px solid ${UI.line.strong}`,
  },
};
